/**
 * ParcelResearchTool — editable full-page parcel research workspace (CT-020, Phase 2).
 *
 * Full-screen overlay opened from the parcel ⋯ menu ("Open Research Tool",
 * event `open-parcel-research-tool` with detail.parcelId). Left nav = research
 * subsections; right pane = editable fields. Edits PATCH Airtable on blur/change
 * via services/parcels; formula fields are read-only and refresh after each save.
 *
 * Existing Approvals (Historical Permits) are managed inline through
 * AddHistoricalPermitModal + delete.
 *
 * Multi-parcel: a project's parcels are listed in the header; the primary parcel
 * (lowest Project Parcel ID) drives the pre-design report.
 *
 * Zone narrative editing (setbacks/height/coverage) and Jurisdiction/Department editing
 * moved to the Permitting workspace (2026-09, see docs/PERMITTING_WORKSPACE.md) — this panel's
 * own "Zone Standards" block used stale, incorrect field names (`'Front Yard Setback (from
 * Zone)'` — that suffix only exists on Parcels lookups, not on the Zone record itself; every
 * save through it silently failed) and only ever exposed 5 of the ~13 zone narrative fields.
 * The Permitting workspace's zone editor is the one place to edit that content now; this panel
 * keeps the Zone PICKER (which zone a parcel is assigned to) since that's parcel-specific.
 *
 * THE INPUT TOOL (Matt, 2026-09-29): this is where parcel research is entered; the outline's
 * parcel-detail view is a read-out that points here. Every parcel-owned link has a picker:
 * Owner (Organization), Jurisdiction (parcel `Jurisdictions`), Zone, Special Overlay (a second
 * link into Zones), HOA. Picking a zone also writes the zone's city into `Jurisdictions`, since
 * the parcel's GIS-link lookup reads through that link. City-wide values (GIS link, municipal
 * code, type) are shown read-only with "Edit in Permitting", which opens that city's page.
 *
 * Deferred to backlog (see docs/PARCEL_RESEARCH.md): the project-level "Department Reviews
 * Required" editor.
 */
import HeightRulesPanel from './HeightRulesPanel.jsx'
import SetbackMapPanel from './SetbackMapPanel.jsx'
import { useParcelExhibits, ExhibitStrip, ExhibitsPane } from './ParcelExhibits.jsx'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { confirmDialog } from './Dialog.jsx'
import MarkdownField from './MarkdownField.jsx'
import {
  getParcel, getParcelDetail, getParcels, getHistoricalPermits, getJurisdictionAgencies,
  getZones, updateParcelField, deleteHistoricalPermit,
  getParcelHoaAnswer, setParcelHoaAnswer, hoaStatusFor, searchHoaOrganizations, getHoaNames,
  searchJurisdictionOptions, zoneDisplayName, createHoaOrganization,
} from '../services/parcels.js'
import { ctx } from '../outline/ctx.js'
import { hasModule, isSeatLoaded } from '../services/tenancy.js'

// HOA question (2026-09-28): three explicit answers, so "we checked, there is no HOA" can never
// be confused with "nobody has looked". Replaces the Airtable `Association Overlay` checkbox.
const HOA_OPTIONS = [
  { value: 'unknown', label: 'Not researched' },
  { value: 'none', label: 'No HOA' },
  { value: 'has', label: 'Has HOA' },
]

const YESNO = ['Yes', 'No']
const YESNOUNK = ['Yes', 'No', 'Unknown']
const LOT_USES = ['Single Family Residence', 'Multi Family Residence', 'Commercial Retail', 'Commercial Hospitality', 'Commercial Dining', 'Commercial Offices']
// Every list here must match the live Airtable choices exactly: saves use typecast, so a label
// that isn't already a choice silently creates a new near-duplicate one (the old list here did
// exactly that; 'Coastal Zone (Local)' in the live schema is a leftover of it).
const OPTIONS = {
  'Coastal Zone': ['Not in Coastal Zone', 'Coastal Zone (State)', 'Coastal Zone (Local Program)', 'Coastal Zone Appealable (Local Program)'],
  'Oceanfront Lot': YESNO,
  'FHSZ': ['Not in Fire Hazard Severity Zone', 'Moderate', 'High', 'Very High'],
  'FHSZ Responsibility': ['Not Applicable', 'CA - Local responsibility Area (LRA)', 'CA - State Responsibility Area (SRA)', 'NV - Wildland Development Areas (WUI)'],
  'Allowed Lot Use': LOT_USES,
  'Existing Lot Use': LOT_USES,
  'Flood Zone or Water Course': YESNOUNK,
  'Landslide Zone': YESNOUNK,
  'Liquefaction': YESNOUNK,
  'Fault Lines': YESNO,
  'Front Curb': YESNO,
  'LB Site Slope Configuration': ['Rear Lot Line Above Street', 'Rear Lot Line Below Street', 'Through Lot'],
}

const NAV = [
  { key: 'property', label: 'Property' },
  { key: 'map', label: 'Map & GIS' },
  { key: 'zoning', label: 'Zoning' },
  { key: 'hazards', label: 'Hazards' },
  { key: 'requirements', label: 'Requirements' },
  { key: 'metrics', label: 'Existing Metrics' },
  { key: 'notes', label: 'Notes' },
  { key: 'approvals', label: 'Existing Approvals' },
  { key: 'jurisdiction', label: 'Jurisdiction & Agencies' },
  { key: 'exhibits', label: 'Exhibits' },
]

const fmtDate = s => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? `${m[2]}/${m[3]}/${m[1]}` : (s || '') }
const has = v => v != null && v !== '' && !(Array.isArray(v) && v.length === 0)

// Safe display: Airtable formula/lookup fields can return {specialValue:'NaN'} or {error:'#ERROR!'}
// objects (uncomputable formulas) — rendering those as a React child throws (error #31).
function disp(v) {
  if (!has(v)) return '—'
  if (Array.isArray(v)) return v.filter(x => x != null).map(disp).join(', ') || '—'
  if (typeof v === 'object') { const sv = v.specialValue; return sv != null && sv !== 'NaN' ? String(sv) : '—' }
  return String(v)
}
// Editable inputs never receive an object/array (would render "[object Object]").
const inputVal = v => (v == null || typeof v === 'object') ? '' : v
// Derived display, with Lot Slope (stored as a fraction) shown as a percent.
function derivedText(field, v) {
  if (field === 'Lot Slope' && has(v) && !isNaN(Number(v))) return (Number(v) * 100).toFixed(2) + '%'
  return disp(v)
}

export default function ParcelResearchTool() {
  const [open, setOpen] = useState(false)
  const [parcelId, setParcelId] = useState('')
  const [detail, setDetail] = useState(null)   // { parcel, zone, jurisdiction }
  const [siblings, setSiblings] = useState([])  // parcels for the project
  const [permits, setPermits] = useState(null)
  const [agencies, setAgencies] = useState(null)
  const [nav, setNav] = useState('property')
  const [savingField, setSavingField] = useState('')
  const [error, setError] = useState('')
  const [hoaAnswer, setHoaAnswer] = useState(undefined)   // undefined = loading; 'none' | 'has' | null
  const [hoaNames, setHoaNames] = useState({})
  const [ownerNames, setOwnerNames] = useState({})
  const draft = useRef({})
  // Exhibits that verify the findings (AT-PZ6S6) — loaded while the tool is open on a parcel.
  const exhibitStore = useParcelExhibits(open ? parcelId : '')

  function loadParcel(id) {
    setParcelId(id)
    setDetail(null); setPermits(null); setAgencies(null); setHoaAnswer(undefined); setHoaNames({}); draft.current = {}
    getParcelHoaAnswer(id).then(a => setHoaAnswer(a)).catch(() => setHoaAnswer(null))
    getParcelDetail(id).then(d => {
      setDetail(d)
      setOwnerNames(d.ownerNames || {})
      const assoc = [].concat(d.parcel.fields['Association'] || []).filter(Boolean)
      if (assoc.length) getHoaNames(assoc).then(setHoaNames)
      const jid = d.jurisdiction && d.jurisdiction.id
      if (jid) getJurisdictionAgencies(jid).then(setAgencies).catch(() => setAgencies([]))
      const pid = Array.isArray(d.parcel.fields['Project']) ? d.parcel.fields['Project'][0] : null
      const puid = d.parcel.fields['Project Record UID (from Project)']
      const projUid = Array.isArray(puid) ? puid[0] : puid
      if (projUid) getParcels(projUid).then(setSiblings).catch(() => {})
    }).catch(() => setError('Could not load the parcel.'))
    getHistoricalPermits(id).then(setPermits).catch(() => setPermits([]))
  }

  useEffect(() => {
    function onOpen(e) {
      const id = (e && e.detail && e.detail.parcelId) || ''
      if (!id) return
      setNav((e.detail.nav && NAV.some(n => n.key === e.detail.nav)) ? e.detail.nav : 'property'); setError('')
      setOpen(true)
      loadParcel(id)
    }
    function onPermits(e) { if (e.detail && e.detail.parcelId === parcelId) getHistoricalPermits(parcelId).then(setPermits) }
    window.addEventListener('open-parcel-research-tool', onOpen)
    window.addEventListener('historical-permits-changed', onPermits)
    return () => {
      window.removeEventListener('open-parcel-research-tool', onOpen)
      window.removeEventListener('historical-permits-changed', onPermits)
    }
  }, [parcelId])

  if (!open) return null

  const f = (detail && detail.parcel && detail.parcel.fields) || {}
  const zone = (detail && detail.zone && detail.zone.fields) || {}
  const juris = (detail && detail.jurisdiction && detail.jurisdiction.fields) || {}
  const zoneName = zone['Name of Zone'] || zone['Name'] || f['Zone String'] || '—'
  const jurisId = (detail && detail.jurisdiction && detail.jurisdiction.id) || null
  const jurisName = juris['Name'] || ''
  const hoaLinked = [].concat(f['Association'] || []).filter(Boolean)
  const hoaStatus = hoaStatusFor(f, hoaAnswer)
  const memberName = (ctx.currentMember && ctx.currentMember.fields && (ctx.currentMember.fields['Member Name'] || ctx.currentMember.fields['Name'])) || null

  const overlays = (detail && detail.overlays) || []
  const ownerIds = [].concat(f['Owner'] || []).filter(Boolean)
  const ownerLabel = ownerIds.map(id => ownerNames[id]).filter(Boolean).join(', ')
  // The zone's own city, when a zone is picked: the parcel's city must agree with it.
  const zoneJurisId = (detail && detail.zone && [].concat(detail.zone.fields['Jurisdiction'] || [])[0]) || null
  const gisUrl = firstOf(juris['Online GIS Parcel Search'])
  const codeUrl = firstOf(juris['Online Municipal Code'])

  // Tell the outline's read-out to refetch: everything entered here lives on records it cached.
  function close() {
    setOpen(false)
    if (parcelId) window.dispatchEvent(new CustomEvent('parcel-research-changed', { detail: { parcelId } }))
  }

  // City-wide facts (GIS link, municipal code, departments) are edited once per city in the
  // Permitting workspace, never per parcel.
  function editCityInPermitting() {
    close()
    window.dispatchEvent(new CustomEvent('open-permitting', { detail: { tool: 'zones', jurisdictionId: jurisId } }))
  }

  // Zone and overlay pickers lead with the parcel's own city (zone names repeat across cities).
  async function searchZoneOptions(q) {
    const rows = await getZones(q)
    return rows
      .sort((a, b) => (b.jurisdiction === jurisId) - (a.jurisdiction === jurisId)
        || (a.jurisdictionName || '').localeCompare(b.jurisdictionName || '') || a.name.localeCompare(b.name))
      .map(z => ({ ...z, sub: [z.jurisdictionName, z.type !== 'Zone' ? z.type : ''].filter(Boolean).join(' · ') }))
  }

  async function reloadDetail() {
    const d = await getParcelDetail(parcelId)
    setDetail(d)
    setOwnerNames(d.ownerNames || {})
    const jid = d.jurisdiction && d.jurisdiction.id
    if (jid) getJurisdictionAgencies(jid).then(setAgencies).catch(() => setAgencies([]))
    else setAgencies([])
  }

  async function pickJurisdiction(j) {
    // A zone from another city would contradict the new city, so clear it (and the overlay).
    const clearZone = zoneJurisId && zoneJurisId !== j.id
    if (clearZone) {
      const ok = await confirmDialog({ title: 'Change the city?', message: `The zone on this parcel (${zoneName}) belongs to ${jurisName || 'another city'}. Changing the city to ${j.name} clears the zone and special overlay so you can pick ones from ${j.name}.`, confirmLabel: 'Change city', danger: true })
      if (!ok) return
    }
    setSavingField('Jurisdiction'); setError('')
    try {
      await updateParcelField(parcelId, { 'Jurisdictions': [j.id], ...(clearZone ? { 'Zone': [], 'Special Overlay': [] } : {}) })
      await reloadDetail()
    } catch (err) { setError('Could not change the city.') }
    setSavingField('')
  }

  async function setLink(field, ids, what) {
    setSavingField(field); setError('')
    try { await updateParcelField(parcelId, { [field]: ids }); await reloadDetail() }
    catch (err) { setError(`Could not save the ${what}.`) }
    setSavingField('')
  }

  async function saveParcel(field, value) {
    setSavingField(field); setError('')
    try {
      await updateParcelField(parcelId, { [field]: value })
      const fresh = await getParcel(parcelId)        // refresh formula fields
      if (fresh) setDetail(d => ({ ...d, parcel: fresh }))
    } catch (err) { setError(`Could not save ${field}.`) }
    setSavingField('')
  }

  async function pickZone(z) {
    setSavingField('Zone'); setError('')
    try {
      // Keep the parcel's own city link in step with the zone's city: the parcel's GIS link
      // lookup reads through that link, not through the zone.
      await updateParcelField(parcelId, { 'Zone': [z.id], ...(z.jurisdiction ? { 'Jurisdictions': [z.jurisdiction] } : {}) })
      await reloadDetail()
    } catch (err) { setError('Could not change the zone.') }
    setSavingField('')
  }

  async function setHoa(next) {
    if (next === hoaStatus) return
    // Moving off "Has HOA" while an HOA is linked would leave the link contradicting the answer.
    if (next !== 'has' && hoaLinked.length) {
      const names = hoaLinked.map(id => hoaNames[id]).filter(Boolean).join(', ') || 'an HOA'
      const ok = await confirmDialog({ title: 'Remove the linked HOA?', message: `This parcel is linked to ${names}. Changing the answer removes that link.`, confirmLabel: 'Remove link', danger: true })
      if (!ok) return
    }
    setSavingField('HOA'); setError('')
    try {
      if (next !== 'has' && hoaLinked.length) {
        await updateParcelField(parcelId, { 'Association': [] })
        const fresh = await getParcel(parcelId)
        if (fresh) setDetail(d => ({ ...d, parcel: fresh }))
      }
      setHoaAnswer(await setParcelHoaAnswer(parcelId, next === 'unknown' ? null : next, memberName))
    } catch (err) { setError('Could not save the HOA answer.') }
    setSavingField('')
  }

  async function linkHoa(org) {
    setSavingField('HOA'); setError('')
    try {
      await updateParcelField(parcelId, { 'Association': [org.id] })
      setHoaNames(n => ({ ...n, [org.id]: org.name }))
      setHoaAnswer(await setParcelHoaAnswer(parcelId, 'has', memberName))
      const fresh = await getParcel(parcelId)
      if (fresh) setDetail(d => ({ ...d, parcel: fresh }))
    } catch (err) { setError('Could not link the HOA.') }
    setSavingField('')
  }

  async function delPermit(p) {
    const ok = await confirmDialog({ title: 'Delete approval?', message: `Delete "${p.fields['Permit Name'] || 'this approval'}"?`, confirmLabel: 'Delete', danger: true })
    if (!ok) return
    await deleteHistoricalPermit(p.id)
    getHistoricalPermits(parcelId).then(setPermits)
  }

  /* ── field renderers ─────────────────────────────────────────────── */
  const S = STYLES
  function Text({ label, field, mono }) {
    const cur = inputVal(f[field])
    return (
      <Row label={label} saving={savingField === field}>
        <input
          key={parcelId + field}
          style={mono ? S.mono : S.input}
          defaultValue={cur}
          onBlur={e => { const v = e.target.value; if (String(v) !== String(cur)) saveParcel(field, v === '' ? null : v) }}
        />
      </Row>
    )
  }
  function Num({ label, field, derived }) {
    if (derived) return <Row label={label} derived><span style={S.derived}>{derivedText(field, f[field])}</span></Row>
    const cur = inputVal(f[field])
    return (
      <Row label={label} saving={savingField === field}>
        <input type="number" style={S.mono}
          key={parcelId + field}
          defaultValue={cur}
          onBlur={e => { const v = e.target.value; if (String(v) !== String(cur)) saveParcel(field, v === '' ? null : Number(v)) }} />
      </Row>
    )
  }
  function Select({ label, field }) {
    const opts = OPTIONS[field] || []
    const cur = typeof f[field] === 'object' ? '' : (f[field] || '')
    const all = cur && !opts.includes(cur) ? [cur, ...opts] : opts
    return (
      <Row label={label} saving={savingField === field}>
        <select style={S.input} value={cur} onChange={e => saveParcel(field, e.target.value || null)}>
          <option value="">—</option>
          {all.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Row>
    )
  }
  function Check({ label, field }) {
    return (
      <Row label={label} saving={savingField === field}>
        <input type="checkbox" checked={!!f[field]} onChange={e => saveParcel(field, e.target.checked)} />
      </Row>
    )
  }

  return createPortal(
    <div style={S.overlay}>
      {/* Header */}
      <div style={S.header}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.kicker}>Parcel Research</div>
          <div style={S.title}>{f['APN'] || f['Name'] || 'Parcel'}{zoneName !== '—' ? ` · ${zoneName}` : ''}{jurisName && zoneName !== '—' ? <span style={S.titleCity}>{` · ${jurisName}`}</span> : null}</div>
        </div>
        {siblings.length > 1 && (
          <select style={{ ...S.input, width: 200, marginRight: 10 }} value={parcelId} onChange={e => loadParcel(e.target.value)}>
            {siblings.map(p => <option key={p.id} value={p.id}>{`Parcel ${p.fields['Project Parcel ID'] || ''} — ${p.fields['APN'] || p.id}`.trim()}</option>)}
          </select>
        )}
        <button style={S.ghostBtn} onClick={() => window.dispatchEvent(new CustomEvent('generate-predesign-report', { detail: { projectId: Array.isArray(f['Project']) ? f['Project'][0] : null } }))} title="Generate the pre-design report">Generate Report</button>
        <button style={S.closeBtn} onClick={close}>✕</button>
      </div>

      {error && <div style={S.error}>{error}</div>}

      <div style={S.body}>
        {/* Left nav */}
        <div style={S.nav}>
          {NAV.map(n => (
            <button key={n.key} style={{ ...S.navItem, ...(nav === n.key ? S.navItemActive : {}) }} onClick={() => setNav(n.key)}>{n.label}</button>
          ))}
        </div>

        {/* Right pane */}
        <div style={S.pane}>
          {!detail && <div style={S.loading}>Loading parcel…</div>}
          {detail && nav === 'property' && (<>
            <Text label="APN" field="APN" mono />
            <Text label="Legal Description" field="Legal Description" />
            <Num label="Lot Size SF" field="Lot Size SF" />
            <Row label="Lot Size Acres" derived><span style={S.derived}>{disp(f['Lot Size Acres'])}</span></Row>
            <Row label="Property Owner" saving={savingField === 'Owner'}>
              <RecordPicker
                current={ownerIds.length ? (ownerLabel || 'Linked owner') : ''}
                placeholder="Search organizations…"
                emptyText="No matching organizations. Add the owner in Contacts first."
                search={searchHoaOrganizations}
                onPick={o => setLink('Owner', [o.id], 'owner')}
                onClear={() => setLink('Owner', [], 'owner')}
                disabled={savingField === 'Owner'}
              />
            </Row>
            <CityRow label="GIS Link" jurisName={jurisName} onEdit={editCityInPermitting}>
              {gisUrl ? <a href={gisUrl} target="_blank" rel="noopener noreferrer" style={S.link}>Open county GIS ↗</a> : <span style={S.derived}>—</span>}
            </CityRow>
          </>)}

          {detail && nav === 'map' && <ParcelMap f={f} juris={juris} zone={zone} />}

          {detail && nav === 'zoning' && (<>
            <Row label="Jurisdiction" saving={savingField === 'Jurisdiction'}>
              <RecordPicker
                current={jurisName}
                placeholder="Search cities and counties…"
                emptyText={<PermittingHubHelp kind="jurisdiction" />}
                search={searchJurisdictionOptions}
                onPick={pickJurisdiction}
                note={zoneJurisId ? 'Set by the zone. Picking a different city clears the zone.' : 'Pick the city first so its zones lead the zone search.'}
                disabled={savingField === 'Jurisdiction'}
              />
            </Row>
            <Row label="Zone" saving={savingField === 'Zone'}>
              <RecordPicker
                current={zoneName !== '—' ? zoneName : ''}
                currentSub={jurisName && zoneName !== '—' ? jurisName : ''}
                placeholder="Search zones…"
                emptyText={<PermittingHubHelp kind="zone" />}
                search={searchZoneOptions}
                onPick={pickZone}
                disabled={savingField === 'Zone'}
              />
            </Row>
            <ExhibitStrip parcelId={parcelId} topic="zoning" store={exhibitStore} memberName={memberName} />
            <Row label="Special Overlay" saving={savingField === 'Special Overlay'}>
              <RecordPicker
                current={overlays.map(o => zoneDisplayName(o.fields)).filter(Boolean).join(', ')}
                placeholder="Search overlays and districts…"
                emptyText={<PermittingHubHelp kind="overlay" />}
                search={searchZoneOptions}
                onPick={z => setLink('Special Overlay', [z.id], 'special overlay')}
                onClear={() => setLink('Special Overlay', [], 'special overlay')}
                note="Leave blank when no overlay applies."
                disabled={savingField === 'Special Overlay'}
              />
            </Row>
            <ExhibitStrip parcelId={parcelId} topic="overlay" store={exhibitStore} memberName={memberName} />
            <Select label="Allowed Lot Use" field="Allowed Lot Use" />
            <Select label="Existing Lot Use" field="Existing Lot Use" />
            <Row label="HOA" saving={savingField === 'HOA'}>
              <div style={{ flex: 1 }}>
                <div style={S.segWrap}>
                  {HOA_OPTIONS.map(o => (
                    <button key={o.value} disabled={hoaAnswer === undefined || savingField === 'HOA'}
                      style={{ ...S.seg, ...(hoaStatus === o.value ? S.segOn : {}) }}
                      onClick={() => setHoa(o.value)}>{o.label}</button>
                  ))}
                </div>
                {hoaStatus === 'has' && (
                  <HoaPicker linkedIds={hoaLinked} names={hoaNames} onPick={linkHoa} disabled={savingField === 'HOA'} />
                )}
                {hoaStatus === 'has' && hoaLinked.map(id => (
                  <HeightRulesPanel key={id} kind="organization" sourceId={id} sourceName={hoaNames[id] || ''} byName={memberName} />
                ))}
              </div>
            </Row>
          </>)}

          {detail && nav === 'hazards' && (<>
            <Select label="Coastal Zone" field="Coastal Zone" />
            <Select label="Oceanfront Lot" field="Oceanfront Lot" />
            <ExhibitStrip parcelId={parcelId} topic="coastal" store={exhibitStore} memberName={memberName} />
            <Select label="FHSZ" field="FHSZ" />
            <Select label="FHSZ Responsibility" field="FHSZ Responsibility" />
            <ExhibitStrip parcelId={parcelId} topic="fhsz" store={exhibitStore} memberName={memberName} />
            <Select label="Flood Zone or Water Course" field="Flood Zone or Water Course" />
            <ExhibitStrip parcelId={parcelId} topic="flood" store={exhibitStore} memberName={memberName} />
            <Select label="Landslide Zone" field="Landslide Zone" />
            <ExhibitStrip parcelId={parcelId} topic="landslide" store={exhibitStore} memberName={memberName} />
            <Select label="Liquefaction" field="Liquefaction" />
            <ExhibitStrip parcelId={parcelId} topic="liquefaction" store={exhibitStore} memberName={memberName} />
            <Select label="Fault Lines" field="Fault Lines" />
            <ExhibitStrip parcelId={parcelId} topic="fault" store={exhibitStore} memberName={memberName} />
          </>)}

          {detail && nav === 'requirements' && (<>
            <SetbackMapPanel parcelId={parcelId} parcelFields={f} memberName={memberName} />
            <Num label="Required Front Yard Setback" field="Required Front Yard Setback" />
            <Num label="Required Rear Yard Setback" field="Required Rear Yard Setback" />
            <Num label="Required Side Yard Setback 1" field="Required Side Yard Setback 1" />
            <Num label="Required Side Yard Setback 2" field="Required Side Yard Setback 2" />
            <Num label="Required Covered Parking Spaces" field="Required Covered Parking Spaces" />
            <Num label="Required Uncovered Parking Spaces" field="Required Uncovered Parking Spaces" />
            <Num label="Required Open Space Percent" field="Required Open Space Percent" />
            <Num label="Required Open Space SF" field="Required Open Space SF" derived />
            <Num label="Allowable Height from Grade" field="Allowable Height from Grade Manual" />
            <Num label="Allowable Height from Rear Lot Line" field="Allowable Height from Rear Lot Line Manual" />
            <Num label="Allowable Height from Front Lot Line" field="Allowable Height from Front Lot Line Manual" />
          </>)}

          {detail && nav === 'metrics' && (<>
            <Num label="Average Lot Depth" field="Average Lot Depth" />
            <Num label="Average Lot Width" field="Average Lot Width" />
            <Num label="Elevation — High Side Corner 1" field="Lot High Corner 1 Elevation" />
            <Num label="Elevation — High Side Corner 2" field="Lot High Corner 2 Elevation" />
            <Num label="Elevation — Low Side Corner 1" field="Lot Low Corner 1 Elevation" />
            <Num label="Elevation — Low Side Corner 2" field="Lot Low Corner 2 Elevation" />
            <Num label="Elev. at Midpoint of Front Curb / CL" field="Height at Midpoint of Front Curb or CL of Travelled Way" />
            <Num label="Elev. at Natural Grade 40' in" field="Height of Natural Grade 40 Feet in from Midpoint of Front Lot Line" />
            <Num label="Lot Slope" field="Lot Slope" derived />
            <Select label="Site Slope Configuration" field="LB Site Slope Configuration" />
            <Select label="Front Curb" field="Front Curb" />
            <Num label="Existing Building Height from Grade FF" field="Existing Height from Grade FF" />
            <Num label="Existing Building Height from Slope Datum" field="Existing Height from Slope Datum" />
            <Check label="Existing Height Approximate" field="Existing Height Approximate" />
            <Num label="Existing Lot Coverage SF" field="Existing Lot Coverage SF" />
            <Num label="Existing Lot Coverage %" field="Existing Lot Coverage" derived />
            <Num label="Allowable Lot Coverage %" field="Allowable Lot Coverage Percent" derived />
            <Num label="Allowable Lot Coverage SF" field="Allowable Lot Coverage SF" derived />
            <Num label="Existing Front Yard Setback" field="Existing Front Yard Setback" />
            <Num label="Existing Rear Yard Setback" field="Existing Rear Yard Setback" />
            <Num label="Existing Side Yard Setback 1" field="Existing Side Yard Setback 1" />
            <Num label="Existing Side Yard Setback 2" field="Existing Side Yard Setback 2" />
            <Check label="Existing Setbacks Approximate" field="Existing Setbacks Approximate" />
            <Num label="Existing Covered Parking Spaces" field="Existing Covered Parking Spaces" />
            <Num label="Existing Uncovered Parking Spaces" field="Existing Uncovered Parking Spaces" />
          </>)}

          {detail && nav === 'notes' && (
            <div style={{ maxWidth: 640 }}>
              <div style={S.label}>Project Specific Permitting Notes</div>
              <NotesEditor value={f['Project Specific Permitting Notes'] || ''} onSave={v => saveParcel('Project Specific Permitting Notes', v || null)} />
            </div>
          )}

          {detail && nav === 'approvals' && (
            <div>
              <button style={S.primaryBtn} onClick={() => window.dispatchEvent(new CustomEvent('open-historical-permit-modal', { detail: { parcelId } }))}>Add Approval</button>
              {permits == null && <div style={S.loading}>Loading approvals…</div>}
              {permits && permits.length === 0 && <div style={S.loading}>No historical permits.</div>}
              {permits && permits.map(p => (
                <div key={p.id} style={S.permitRow}>
                  <span style={{ fontFamily: '"DM Mono",monospace', width: 90 }}>{fmtDate(p.fields['Permit Date'])}</span>
                  <span style={{ flex: 1 }}>{p.fields['Permit Name'] || '—'}{p.fields['Reference Number'] ? ` · #${p.fields['Reference Number']}` : ''}{p.fields['Design Review'] ? ' · DR' : ''}</span>
                  <button style={S.linkBtn} onClick={() => window.dispatchEvent(new CustomEvent('open-historical-permit-modal', { detail: { parcelId, permit: p } }))}>Edit</button>
                  <button style={{ ...S.linkBtn, color: '#c0392b' }} onClick={() => delPermit(p)}>Delete</button>
                </div>
              ))}
            </div>
          )}

          {detail && nav === 'exhibits' && <ExhibitsPane parcelId={parcelId} store={exhibitStore} memberName={memberName} />}

          {detail && nav === 'jurisdiction' && (<>
            {!jurisId && (
              <div style={S.cityNote}>
                No city yet. Pick the Jurisdiction (or a Zone) under <button style={S.linkBtn} onClick={() => setNav('zoning')}>Zoning</button> and these fill in from that city.
              </div>
            )}
            {jurisId && (
              <div style={S.cityNote}>
                These are the same for every parcel in {jurisName || 'this city'}, so they are edited once, on the city's page in Permitting Hub.{' '}
                <button style={S.linkBtn} onClick={editCityInPermitting}>Edit {jurisName || 'city'} in Permitting Hub</button>
              </div>
            )}
            <Row label="Jurisdiction" derived><span style={S.derived}>{juris['Name'] || '—'}</span></Row>
            <Row label="Type" derived><span style={S.derived}>{disp(juris['Type'])}</span></Row>
            <Row label="Municipal Code" derived>{codeUrl ? <a href={codeUrl} target="_blank" rel="noopener noreferrer" style={S.link}>Open municipal code ↗</a> : <span style={S.derived}>—</span>}</Row>
            <Row label="GIS Parcel Search" derived>{gisUrl ? <a href={gisUrl} target="_blank" rel="noopener noreferrer" style={S.link}>Open county GIS ↗</a> : <span style={S.derived}>—</span>}</Row>
            <div style={{ ...S.label, marginTop: 18 }}>Agencies</div>
            {agencies == null && <div style={S.loading}>Loading agencies…</div>}
            {agencies && agencies.length === 0 && <div style={S.loading}>No agencies linked to this jurisdiction.</div>}
            {agencies && agencies.map(a => (
              <div key={a.id} style={S.agencyCard}>
                <div style={{ fontWeight: 500 }}>{a.fields['Organization Name'] || 'Agency'} <span style={{ color: 'var(--aj-text-muted)', fontWeight: 400 }}>{a.fields['Agency Type'] || ''}</span></div>
                <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 12, color: 'var(--aj-text-mid)' }}>
                  {[a.fields['Phone'], a.fields['General Email'], a.fields['Submission Method']].filter(has).join('  ·  ') || '—'}
                </div>
              </div>
            ))}
          </>)}
        </div>
      </div>
    </div>,
    document.body
  )
}

/**
 * ParcelMap — embedded map centered on the parcel address (or APN + jurisdiction) plus quick links
 * to the county GIS parcel search and the online municipal code. Uses the keyless Google Maps embed
 * (maps.google.com/maps?q=…&output=embed) — interactive, with a satellite toggle, no API key.
 * Pulling parcel geometry/zoning overlays programmatically is jurisdiction-specific (backlog).
 */
function ParcelMap({ f, juris, zone }) {
  const addr = (Array.isArray(f['Address (From Project)']) ? f['Address (From Project)'][0] : f['Address (From Project)']) || ''
  const query = (addr || [f['APN'], juris['Name'], 'CA'].filter(has).join(' ')).replace(/\n/g, ', ')
  const mapSrc = 'https://maps.google.com/maps?q=' + encodeURIComponent(query) + '&z=18&t=k&output=embed'
  const gisUrl = (Array.isArray(juris['Online GIS Parcel Search']) ? juris['Online GIS Parcel Search'][0] : juris['Online GIS Parcel Search'])
    || (Array.isArray(f['Online GIS Parcel Search']) ? f['Online GIS Parcel Search'][0] : f['Online GIS Parcel Search']) || ''
  const codeUrl = (Array.isArray(juris['Online Municipal Code']) ? juris['Online Municipal Code'][0] : juris['Online Municipal Code']) || ''
  return (
    <div style={{ maxWidth: 880 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 12, color: 'var(--aj-text-mid)' }}>{query || '—'}</span>
        {gisUrl && <a href={gisUrl} target="_blank" rel="noopener noreferrer" style={STYLES.ghostBtn}>Open county GIS ↗</a>}
        {codeUrl && <a href={codeUrl} target="_blank" rel="noopener noreferrer" style={STYLES.ghostBtn}>Municipal code ↗</a>}
        <a href={'https://maps.google.com/maps?q=' + encodeURIComponent(query)} target="_blank" rel="noopener noreferrer" style={STYLES.ghostBtn}>Open in Maps ↗</a>
      </div>
      <iframe
        title="Parcel map"
        src={mapSrc}
        style={{ width: '100%', height: 460, border: '0.5px solid var(--aj-border-mid)', borderRadius: 8 }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 8 }}>
        Map centered on the parcel address. Live parcel-boundary / zoning-overlay GIS layers are jurisdiction-specific — see the backlog in docs/PARCEL_RESEARCH.md.
      </div>
    </div>
  )
}

/**
 * HoaPicker — shows the linked HOA (an Organization) and lets the user search for and link one.
 * Top-level (not defined inside ParcelResearchTool) so its search state survives parent renders.
 */
function HoaPicker({ linkedIds, names, onPick, disabled }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState(null)
  const [changing, setChanging] = useState(false)
  const reqId = useRef(0)
  const linkedName = linkedIds.map(id => names[id]).filter(Boolean).join(', ')
  const showSearch = !linkedIds.length || changing

  function search(v) {
    setQ(v)
    if (v.trim().length < 2) { setResults(null); return }
    const my = ++reqId.current
    searchHoaOrganizations(v.trim()).then(r => { if (my === reqId.current) setResults(r) }).catch(() => setResults([]))
  }
  function pick(o) { setQ(''); setResults(null); setChanging(false); onPick(o) }
  // Adding an HOA used to mean leaving for Contacts, where a new record could drop out of the
  // list after saving and read as a failed save (2026-09-30, Patch Residence). Create it here.
  const [creating, setCreating] = useState(false)
  const name = q.trim()
  const exact = (results || []).some(o => String(o.name || '').trim().toLowerCase() === name.toLowerCase())
  async function createNew() {
    setCreating(true)
    try { pick(await createHoaOrganization(name)) }
    catch { setResults([]) }
    setCreating(false)
  }

  return (
    <div style={{ marginTop: 8 }}>
      {linkedIds.length > 0 && !changing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--aj-text)' }}>{linkedName || 'Linked HOA'}</span>
          <button style={STYLES.linkBtn} disabled={disabled} onClick={() => setChanging(true)}>Change</button>
        </div>
      )}
      {showSearch && (<>
        {!linkedIds.length && <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 4 }}>Which HOA? Link it so the pre-design report includes it.</div>}
        <input style={STYLES.input} placeholder="Search organizations…" value={q} disabled={disabled} onChange={e => search(e.target.value)} autoFocus={changing} />
        {results && (results.length > 0 || name.length >= 2) && (
          <div style={STYLES.zoneList}>
            {results.map(o => <button key={o.id} style={STYLES.zoneOpt} onClick={() => pick(o)}>{o.name}</button>)}
            {name.length >= 2 && !exact && (
              <button style={{ ...STYLES.zoneOpt, color: '#1d6fa5' }} disabled={creating || disabled} onClick={createNew}>
                {creating ? 'Adding…' : `+ Add "${name}" as a new HOA`}
              </button>
            )}
          </div>
        )}
        {results && results.length > 0 && <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 4 }}>Pick an existing one if it is already listed, so the HOA is not entered twice.</div>}
        {changing && <button style={{ ...STYLES.linkBtn, marginTop: 4 }} onClick={() => { setChanging(false); setQ(''); setResults(null) }}>Cancel</button>}
      </>)}
    </div>
  )
}

const firstOf = v => (Array.isArray(v) ? v[0] : v) || ''

/** A city-wide value shown on a parcel page: read here, edited once in Permitting. */
function CityRow({ label, jurisName, onEdit, children }) {
  return (
    <Row label={label}>
      {children}
      {jurisName && <button style={STYLES.linkBtn} onClick={onEdit} title={`Same for every parcel in ${jurisName}`}>Edit in Permitting Hub</button>}
    </Row>
  )
}

/**
 * RecordPicker — shows the linked record and lets the user search for and link another.
 * `search(q)` → [{ id, name, sub? }]. `onClear` (optional) adds a Remove action.
 * Top-level (not defined inside ParcelResearchTool) so its search state survives parent renders.
 */
function RecordPicker({ current, currentSub, placeholder, emptyText, search, onPick, onClear, note, disabled }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState(null)
  const [changing, setChanging] = useState(false)
  const reqId = useRef(0)
  const showSearch = !current || changing

  function run(v) {
    setQ(v)
    if (v.trim().length < 2) { setResults(null); return }
    const my = ++reqId.current
    search(v.trim())
      .then(r => { if (my === reqId.current) setResults(r || []) })
      .catch(() => { if (my === reqId.current) setResults([]) })
  }
  function pick(o) { setQ(''); setResults(null); setChanging(false); onPick(o) }

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {current && !changing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--aj-text)' }}>{current}{currentSub ? <span style={STYLES.zoneCity}>{currentSub}</span> : null}</span>
          <button style={STYLES.linkBtn} disabled={disabled} onClick={() => setChanging(true)}>Change</button>
          {onClear && <button style={{ ...STYLES.linkBtn, color: 'var(--aj-text-muted)' }} disabled={disabled} onClick={onClear}>Remove</button>}
        </div>
      )}
      {showSearch && (<>
        <input style={{ ...STYLES.input, width: '100%' }} placeholder={placeholder} value={q} disabled={disabled} onChange={e => run(e.target.value)} autoFocus={changing} />
        {results && results.length === 0 && (typeof emptyText === 'string'
          ? <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 4 }}>{emptyText}</div>
          : emptyText)}
        {results && results.length > 0 && (
          <div style={STYLES.zoneList}>
            {results.map(o => (
              <button key={o.id} style={STYLES.zoneOpt} onClick={() => pick(o)}>
                {o.name}{o.sub ? <span style={STYLES.zoneCity}>{o.sub}</span> : null}
              </button>
            ))}
          </div>
        )}
        {changing && <button style={{ ...STYLES.linkBtn, marginTop: 4 }} onClick={() => { setChanging(false); setQ(''); setResults(null) }}>Cancel</button>}
      </>)}
      {note && <div style={{ fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 4 }}>{note}</div>}
    </div>
  )
}

/**
 * PermittingHubHelp — the "nothing matched" line under the Jurisdiction / Zone / Overlay pickers,
 * with a step-by-step walkthrough of adding the missing record in Permitting Hub (Rafael asked
 * where that is, 2026-10-19). Jurisdictions, zones and overlays are shared firm records, so they
 * are created there, not here. Permitting Hub is manager-and-up (`permitting` module); anyone
 * without it is told who can add it instead of being sent to a menu item they can't see.
 */
function PermittingHubHelp({ kind }) {
  const [open, setOpen] = useState(false)
  const canOpen = isSeatLoaded() && (hasModule('permitting') || hasModule('admin'))
  const what = kind === 'jurisdiction' ? 'jurisdiction' : kind === 'zone' ? 'zone' : 'overlay or district'

  const steps = kind === 'jurisdiction' ? [
    'Search by the city or county name alone, e.g. "Laguna Beach", not "City of Laguna Beach". If it shows up, pick it and you are done.',
    'Still nothing? Open Permitting Hub (button below, or the left menu → Permitting Hub). It opens on the Zones & Jurisdictions tab.',
    'Search the list on the left first, so you don\'t add a duplicate.',
    'Click + New Jurisdiction. Type the name ("Laguna Beach"), choose the Type (City or County) and the State, then Create.',
    'Add its zones while you are there: with the new jurisdiction selected, click + New Zone in the middle column for each zone the project needs.',
    'Come back to this tab and search again. The new jurisdiction appears right away.',
  ] : [
    kind === 'zone'
      ? 'Pick the Jurisdiction above first. Its zones come up first when you search here.'
      : 'Pick the Jurisdiction above first so you know whose overlays to look under.',
    'Open Permitting Hub (button below, or the left menu → Permitting Hub). It opens on the Zones & Jurisdictions tab.',
    'Find the city or county in the list on the left and click it. If it is not there, click + New Jurisdiction and add it first.',
    kind === 'zone'
      ? 'In the middle column, click + New Zone. Type the zone name as the city writes it (e.g. "R-1"), leave Type as Zone, then Create.'
      : 'In the middle column, click + New Zone. Type its name as the city writes it, set Type to Special Overlay, then Create.',
    'Fill in the standards on the right (setbacks, height, coverage, code citations) if you have them. They can also wait.',
    'Come back to this tab and search again. It appears right away.',
  ]

  function openHub() {
    const url = new URL(window.location.origin + window.location.pathname)
    url.searchParams.set('ws', 'permitting')
    url.searchParams.set('wsTool', 'zones')
    window.open(url.toString(), '_blank')
  }

  return (
    <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 4 }}>
      No matching {kind === 'overlay' ? 'overlays' : what + 's'}.{' '}
      <button style={STYLES.linkBtn} onClick={() => setOpen(o => !o)}>
        {open ? 'Hide steps' : `How do I add a ${what}?`}
      </button>
      {open && (
        <div style={{ marginTop: 6, padding: '10px 12px', border: '0.5px solid var(--aj-border)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)' }}>
          <div style={{ fontSize: 12, marginBottom: 6, color: 'var(--aj-text-mid)' }}>
            Jurisdictions, zones and overlays are shared by every project, so they are added once in Permitting Hub and then picked here.
          </div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.55 }}>
            {steps.map((s, i) => <li key={i} style={{ marginBottom: 3 }}>{s}</li>)}
          </ol>
          {canOpen ? (
            <button
              onClick={openHub}
              style={{ marginTop: 8, padding: '5px 12px', fontSize: 12, fontFamily: '"DM Sans",sans-serif', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' }}
            >
              Open Permitting Hub in a new tab ↗
            </button>
          ) : (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--aj-text-mid)' }}>
              Permitting Hub is open to managers and admins. Ask one of them to add the {what}, or to give you access, then search again here.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Markdown editor with a Save button (saves on demand; preview via MarkdownField). */
function NotesEditor({ value, onSave, saving }) {
  const [val, setVal] = useState(value)
  const [dirty, setDirty] = useState(false)
  useEffect(() => { setVal(value); setDirty(false) }, [value])
  return (
    <div>
      <MarkdownField value={val} onChange={v => { setVal(v); setDirty(true) }} placeholder="Type Markdown…" />
      {dirty && (
        <button style={{ ...STYLES.primaryBtn, marginTop: 6 }} disabled={saving} onClick={() => onSave(val)}>{saving ? 'Saving…' : 'Save'}</button>
      )}
    </div>
  )
}

function Row({ label, children, saving, derived }) {
  return (
    <div style={STYLES.row}>
      <div style={STYLES.rowLabel}>{label}{derived && <span style={STYLES.derivedTag}>derived</span>}</div>
      <div style={STYLES.rowField}>{children}{saving && <span style={STYLES.savingTag}>saving…</span>}</div>
    </div>
  )
}

const STYLES = {
  overlay: { position: 'fixed', inset: 0, background: 'var(--aj-bg-page)', zIndex: 8000, display: 'flex', flexDirection: 'column', fontFamily: '"DM Sans",sans-serif' },
  header: { display: 'flex', alignItems: 'center', gap: 8, padding: '14px 22px', borderBottom: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-elevated)' },
  kicker: { fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', fontWeight: 500 },
  title: { fontSize: 16, fontWeight: 500, color: 'var(--aj-text)' },
  body: { flex: 1, display: 'flex', minHeight: 0 },
  nav: { width: 200, borderRight: '0.5px solid var(--aj-border)', padding: '12px 0', background: 'var(--aj-bg-elevated)', overflowY: 'auto' },
  navItem: { display: 'block', width: '100%', textAlign: 'left', padding: '9px 22px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--aj-text-mid)', fontFamily: '"DM Sans",sans-serif' },
  navItemActive: { background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', fontWeight: 500, boxShadow: 'inset 3px 0 0 var(--aj-text)' },
  pane: { flex: 1, overflowY: 'auto', padding: '22px 28px' },
  loading: { color: 'var(--aj-text-muted)', fontSize: 13, padding: '8px 0' },
  row: { display: 'flex', alignItems: 'center', gap: 14, padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)', maxWidth: 720 },
  rowLabel: { flex: '0 0 260px', maxWidth: 260, fontSize: 12, color: 'var(--aj-text-mid)', display: 'flex', alignItems: 'center', gap: 6 },
  rowField: { flex: 1, display: 'flex', alignItems: 'center', gap: 8 },
  label: { fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 5 },
  input: { flex: 1, minWidth: 0, boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' },
  mono: { flex: 1, minWidth: 0, boxSizing: 'border-box', fontFamily: '"DM Mono",monospace', fontSize: 13, padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' },
  derived: { fontFamily: '"DM Mono",monospace', fontSize: 13, color: 'var(--aj-text-mid)' },
  derivedTag: { fontFamily: '"DM Mono",monospace', fontSize: 8, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', border: '0.5px solid #7a9aaa55', borderRadius: 20, padding: '1px 6px' },
  savingTag: { fontSize: 10, color: 'var(--aj-text-muted)' },
  error: { background: 'rgba(192,57,43,0.14)', color: '#c0392b', fontSize: 12, padding: '6px 22px' },
  closeBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 16, padding: '4px 8px' },
  ghostBtn: { border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)', cursor: 'pointer', borderRadius: 4, padding: '8px 14px', fontSize: 12, fontWeight: 500, fontFamily: '"DM Sans",sans-serif' },
  primaryBtn: { border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer', borderRadius: 4, padding: '8px 16px', fontSize: 12, fontWeight: 500, fontFamily: '"DM Sans",sans-serif', marginBottom: 12 },
  linkBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: '#1d6fa5', fontSize: 12, fontFamily: '"DM Sans",sans-serif' },
  zoneList: { border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, marginTop: 4, maxHeight: 180, overflowY: 'auto', background: 'var(--aj-bg-elevated)' },
  link: { color: '#1d6fa5', fontSize: 13, textDecoration: 'none', fontFamily: '"DM Sans",sans-serif' },
  cityNote: { fontSize: 12, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '8px 12px', marginBottom: 12, maxWidth: 720 },
  zoneCity: { marginLeft: 8, fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif' },
  titleCity: { fontSize: 13, color: 'var(--aj-text-muted)', fontWeight: 400 },
  segWrap: { display: 'inline-flex', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, overflow: 'hidden' },
  seg: { border: 'none', borderRight: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text-mid)', padding: '6px 12px', fontSize: 12, fontFamily: '"DM Sans",sans-serif', cursor: 'pointer' },
  segOn: { background: 'var(--aj-accent)', color: 'var(--aj-bg-surface)', fontWeight: 500 },
  zoneOpt: { display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '0.5px solid var(--aj-border)', background: 'transparent', cursor: 'pointer', padding: '7px 9px', fontSize: 12, fontFamily: '"DM Sans",sans-serif', color: 'var(--aj-text)' },
  permitRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '0.5px solid var(--aj-border)', fontSize: 13, color: 'var(--aj-text)', maxWidth: 720 },
  agencyCard: { padding: '10px 12px', border: '0.5px solid var(--aj-border)', borderRadius: 8, marginBottom: 8, maxWidth: 560 },
}
